import React, { useRef, useState } from 'react';
import {
    ButtonGroup,
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import { css } from '@emotion/react';
import { PrimaryButton } from '@/styles/blocks';

/**
 * Export button that grows a format picker when more than one option is available.
 *
 * With a single option it renders a plain button, so a page with no extra export formats looks and
 * behaves exactly as it did before.
 *
 * @param {string[]} options      Format labels. The first is the default.
 * @param {number}   selectedIndex Index of the currently selected format.
 * @param {Function} onSelect     Called with the newly picked index.
 * @param {Function} onExport     Called with the selected index when the main button is pressed.
 * @param {object}   buttonCss    Emotion styles applied to the button(s), for call sites that need
 *                                to match surrounding layout.
 */
const ExportSplitButton = ({
    options,
    selectedIndex = 0,
    onSelect,
    onExport,
    disabled = false,
    loading = false,
    buttonCss,
}) => {
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    // Guard against an index left over from a larger option list.
    const index = selectedIndex < options.length ? selectedIndex : 0;

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    if (options.length <= 1) {
        return (
            <PrimaryButton loading={loading} onClick={() => onExport(0)} disabled={disabled} css={buttonCss}>
                {options[0]}
            </PrimaryButton>
        );
    }

    return (
        <>
            <ButtonGroup variant="contained" ref={anchorRef}>
                <PrimaryButton loading={loading} onClick={() => onExport(index)} disabled={disabled} css={buttonCss}>
                    {options[index]}
                </PrimaryButton>
                <PrimaryButton
                    size="small"
                    onClick={() => setOpen((prev) => !prev)}
                    disabled={disabled}
                    css={[
                        buttonCss,
                        css`
                            min-width: 0px;
                            margin-left: 0;
                        `,
                    ]}
                >
                    <ArrowDropDownIcon />
                </PrimaryButton>
            </ButtonGroup>
            <Popper
                style={{ zIndex: 1 }}
                open={open}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-export" autoFocusItem>
                                    {options.map((option, optionIndex) => (
                                        <MenuItem
                                            key={option}
                                            selected={optionIndex === index}
                                            onClick={() => {
                                                onSelect(optionIndex);
                                                setOpen(false);
                                            }}
                                        >
                                            {option}
                                        </MenuItem>
                                    ))}
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
        </>
    );
};

export default ExportSplitButton;
